import styled, { css, keyframes } from 'styled-components';

import { SaleCard } from './PremadeRedesign.styles';
import { SHELL_BREAK } from './Shell.styles';

const PHONE = '@media (max-width: 767px)';
const WIDE_ART = '@media (max-width: 1079px)';

/* --------------------------- Gift shop (/gift) --------------------------- */

export const VsPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;
	padding-bottom: 60px;

	.top-gap {
		height: 96px;
	}

	.vs-qn {
		position: absolute;
		top: -5px;
		right: -5px;
		min-width: 18px;
		height: 18px;
		padding: 0 5px;
		border-radius: 9px;
		background: var(--coral);
		color: #fff;
		font: 700 11px/18px var(--fb);
		font-style: normal;
		text-align: center;
	}

	/* same quick-add circle as the live cards; ticked once a copy is in */
	.vs-on {
		background: var(--teal);
		color: var(--teal-deep);
	}

	${SaleCard} {
		box-shadow: none;
	}

	${SHELL_BREAK} {
		.top-gap {
			height: 78px;
		}
	}
`;

/* the gift cards and confetti pieces: /gift hero, order page, homepage */
export const giftCardStyles = css`
	.cf {
		position: absolute;
		width: 7px;
		height: 12px;
		border-radius: 2px;
		opacity: 0.55;
		pointer-events: none;
		rotate: var(--turn);
	}

	.cf.logo {
		width: var(--size);
		height: var(--size);
		border-radius: 0;
	}

	.cf.logo svg {
		display: block;
		width: 100%;
		height: 100%;
	}

	/* QLogo paints in its own fill; here it takes the piece's confetti color */
	.cf.logo path {
		fill: currentColor;
	}

	.stack {
		position: relative;
		height: 290px;
	}

	.vc {
		position: absolute;
		width: 340px;
		aspect-ratio: 16 / 10;
		border-radius: 18px;
		padding: 18px 20px;
		background: ${({ theme }) =>
			theme.mode === 'dark' ? '#f8f0e3' : '#1b1e20'};
		color: ${({ theme }) => (theme.mode === 'dark' ? '#1b140e' : '#f7f7f7')};
		box-shadow: 0 22px 44px
			${({ theme }) =>
				theme.mode === 'dark'
					? 'rgba(0, 0, 0, 0.35)'
					: 'rgba(40, 28, 14, 0.32)'};
		overflow: hidden;
	}

	.vc svg {
		display: block;
		color: ${({ theme }) => (theme.mode === 'dark' ? '#1b140e' : '#f7f7f7')};
	}

	.vc .cf {
		opacity: 0.9;
	}

	.vc:nth-child(1) {
		left: 20px;
		top: 34px;
		transform: rotate(-4deg);
		filter: ${({ theme }) =>
			theme.mode === 'dark' ? 'brightness(0.75)' : 'none'};
	}

	.vc:nth-child(2) {
		left: 60px;
		top: 18px;
		transform: rotate(-1.5deg);
		filter: ${({ theme }) =>
			theme.mode === 'dark' ? 'brightness(0.88)' : 'none'};
	}

	.vc:nth-child(3) {
		left: 110px;
		top: 0;
	}

	/* a dimming filter barely shows on dark cards: set the grays directly */
	${({ theme }) =>
		theme.mode !== 'dark' &&
		`
		.stack .vc:nth-child(1) {
			background: #5c6064;
		}

		.stack .vc:nth-child(2) {
			background: #3a3e41;
		}
	`}

	.vc:not(:last-child) small {
		visibility: hidden;
	}

	.vc b {
		position: relative;
		display: block;
		font-family: var(--fd);
		font-weight: 400;
		text-transform: uppercase;
		font-size: 40px;
		line-height: 0.95;
		margin-top: 28px;
		z-index: 1;
	}

	.vc small {
		position: relative;
		display: block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 24px;
		line-height: 1;
		color: ${({ theme }) => (theme.mode === 'dark' ? '#f16b46' : '#ff7e57')};
		margin-top: 8px;
	}

	${PHONE} {
		.one .vc {
			position: relative;
			left: auto;
			top: auto;
			display: block;
			width: 230px;
			padding: 14px 16px;
			border-radius: 16px;
			transform: rotate(4deg);
			filter: none;
		}

		.one .vc svg {
			width: 66px;
			height: 21px;
		}

		.one .vc b {
			font-size: 27px;
			margin-top: 10px;
		}

		.one .vc small {
			font-size: 18px;
			margin-top: 6px;
		}
	}
`;

export const VsHero = styled.section`
	padding-top: 96px;

	${SHELL_BREAK} {
		padding-top: 72px;
	}

	.art {
		position: relative;
		overflow: hidden;
		display: grid;
		grid-template-columns: 1fr 470px;
		align-items: center;
		gap: 30px;
		height: 430px;
		padding: 0 var(--pad);
		background: ${({ theme }) =>
			theme.mode === 'dark'
				? `radial-gradient(60% 90% at 88% 40%, rgba(0, 255, 194, 0.2), transparent 70%),
				   radial-gradient(50% 80% at 12% 95%, rgba(255, 126, 87, 0.2), transparent 70%),
				   #14191a`
				: `radial-gradient(60% 90% at 88% 40%, rgba(0, 190, 145, 0.2), transparent 70%),
				   radial-gradient(50% 80% at 12% 95%, rgba(241, 107, 70, 0.16), transparent 70%),
				   #f1e6d3`};
	}

	${giftCardStyles}

	h1 {
		position: relative;
		margin: 0;
		color: ${({ theme }) => (theme.mode === 'dark' ? '#f7f7f7' : 'var(--ink)')};
		font-size: clamp(54px, 8vw, 96px);
		line-height: 0.94;
	}

	h1 em {
		display: block;
		font-style: normal;
		color: var(--coral);
	}

	${WIDE_ART} {
		.art {
			grid-template-columns: 1fr;
			justify-items: center;
			align-content: center;
			gap: 56px;
			height: auto;
			padding-top: 72px;
			padding-bottom: 40px;
		}

		h1 {
			text-align: center;
		}

		.stack {
			width: 460px;
			max-width: 100%;
		}
	}

	${PHONE} {
		.art {
			gap: 80px;
			padding-top: 120px;
			padding-bottom: 24px;
		}
	}
`;

/* the gift cards next to the headline on a gift-only order */
export const VsFinishGifts = styled.div`
	${giftCardStyles}

	display: grid;
	grid-template-columns: minmax(0, 1fr) 470px;
	gap: 30px;
	align-items: center;
	padding-right: var(--pad);

	.cards {
		padding-top: 116px;
	}

	${WIDE_ART} {
		grid-template-columns: minmax(0, 1fr);
		gap: 0;
		padding-right: 0;

		.cards {
			display: flex;
			justify-content: center;
			padding: 26px var(--pad) 0;
		}

		.stack {
			width: 460px;
			max-width: 100%;
		}
	}
`;

export const VsHeroCard = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	padding: 24px var(--pad) 26px;
	background: var(--panel-2);

	p {
		margin: 0;
		font-size: 16px;
		color: var(--brown);
	}

	.btns {
		display: flex;
		gap: 12px;
		flex-shrink: 0;
	}

	${PHONE} {
		display: block;
		padding: 18px var(--pad) 20px;

		.btns {
			margin-top: 14px;
		}

		.btns > * {
			flex: 1;
			padding: 13px 10px;
			font-size: 15px;
		}
	}
`;

/* landing first, tabs under it; they stick under the nav once scrolled past */
export const VsTabs = styled.div`
	position: sticky;
	top: 0;
	z-index: 6;
	transition: top 0.3s;

	&.nav {
		top: 74px;
	}

	/* the tabs drop to make room for the returning nav bar: this solid strip
	   keeps the page from showing through above them */
	&.stuck::before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 100%;
		height: 90px;
		background: var(--cream);
		pointer-events: none;
	}

	display: flex;
	justify-content: center;
	margin-top: 36px;
	padding: 12px var(--pad);
	background: var(--cream);

	&.no-hero {
		margin-top: 0;
	}

	& + section,
	& + .sections > section:first-child {
		margin-top: 36px;
	}

	.list {
		display: flex;
		gap: 4px;
		overflow-x: auto;
		scrollbar-width: none;
	}

	.list::-webkit-scrollbar {
		display: none;
	}

	button {
		flex-shrink: 0;
		padding: 8px 15px;
		border: 0;
		border-radius: var(--r-pill);
		background: none;
		color: var(--mut);
		font-family: var(--fd);
		font-size: 14.5px;
		letter-spacing: 0.3px;
		text-transform: uppercase;
		cursor: pointer;
		transition:
			color 0.2s,
			background 0.2s;
	}

	button:hover {
		color: var(--ink);
	}

	button.on {
		background: var(--panel-2);
		color: var(--ink);
	}

	/* on touch widths the bar is a plain strip that scrolls away for good */
	${SHELL_BREAK} {
		&.nav {
			top: 0;
		}
	}

	${PHONE} {
		display: block;
		margin-top: 32px;
		padding: 10px 0 8px;

		& + section,
		& + .sections > section:first-child {
			margin-top: 24px;
		}

		.list {
			padding: 0 10px;
		}

		button {
			font-size: 13.5px;
			padding: 7px 12px;
		}
	}
`;

/* the menu on the page for entering a code, where nothing sits above it */
export const VsRedeemGap = styled.div`
	height: 96px;

	${SHELL_BREAK} {
		height: 78px;
	}
`;

export const VsTopGrid = styled.section`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 22px;
	margin-top: 68px;
	padding: 0 var(--pad);

	${PHONE} {
		grid-template-columns: 1fr 1fr;
		gap: 12px;
		margin-top: 48px;
	}
`;

/* cover and title only: the details popup holds price and add */
export const VsCover = styled.button`
	display: block;
	width: 100%;
	padding: 0;
	border: 0;
	background: none;
	border-radius: 18px;
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.25s;

	&:hover {
		transform: translateY(-4px);
	}

	.ph {
		position: relative;
		display: block;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		background: #17130f;
	}

	/* taller than the cover, so the parallax never shows an edge */
	.drift {
		position: absolute;
		inset: -10% 0;
		display: block;
	}

	img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s;
	}

	&:hover img {
		transform: scale(1.04);
	}

	b {
		position: absolute;
		inset: auto 0 0 0;
		z-index: 1;
		padding: 34px 18px 15px;
		font-family: var(--fd);
		font-weight: 400;
		text-transform: uppercase;
		text-align: left;
		font-size: 24px;
		line-height: 1.04;
		color: #fff;
		text-shadow:
			0 2px 14px rgba(24, 14, 2, 0.85),
			0 1px 3px rgba(24, 14, 2, 0.9);
		background: linear-gradient(
			180deg,
			transparent,
			rgba(24, 14, 2, 0.32) 50%,
			rgba(24, 14, 2, 0.62)
		);
	}

	${PHONE} {
		border-radius: 14px;

		b {
			padding: 24px 10px 9px;
			font-size: 15px;
		}
	}
`;

export const VsSection = styled.section`
	margin-top: 84px;

	&.pad {
		padding: 0 var(--pad);
	}

	&.shelf {
		margin-top: 0;
		padding-top: 96px;
	}

	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 0 var(--pad);
	}

	.head.flat {
		align-items: flex-start;
		padding: 0 0 14px;
	}

	.head.flat > :last-child {
		flex-shrink: 0;
		margin-left: auto;
	}

	h2 {
		margin: 0;
		font-size: 34px;
		line-height: 1;
	}

	.all {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 4px 0;
		border: 0;
		background: none;
		cursor: pointer;
		font-family: var(--fd);
		font-size: 15px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: var(--teal-dark);
		transition: gap 0.2s;
	}

	.all:hover {
		gap: 13px;
	}

	${SHELL_BREAK} {
		&.shelf {
			padding-top: 78px;
		}
	}

	${PHONE} {
		margin-top: 60px;

		.head.flat {
			padding-bottom: 6px;
		}

		h2 {
			font-size: 26px;
		}

		.all {
			font-size: 20px;
			color: var(--ink);
		}

		.all span {
			display: none;
		}
	}
`;

export const VsRail = styled.div`
	position: relative;
	margin-top: 14px;

	.track {
		display: flex;
		align-items: flex-start;
		gap: 20px;
		padding: 6px var(--pad) 26px;
		overflow-x: auto;
		scrollbar-width: none;
		scroll-snap-type: x proximity;
		scroll-padding-left: var(--pad);
	}

	.track::-webkit-scrollbar {
		display: none;
	}

	.track > * {
		flex: 0 0 calc((100% - 60px) / 4);
		min-width: 0;
		scroll-snap-align: start;
	}

	.arrow {
		position: absolute;
		top: 68px;
		z-index: 3;
		width: 42px;
		height: 42px;
		padding: 0;
		display: grid;
		place-items: center;
		border: 0;
		border-radius: 50%;
		background: var(--teal);
		color: var(--teal-deep);
		cursor: pointer;
	}

	.arrow.l {
		left: calc(var(--pad) - 22px);
	}

	.arrow.l svg {
		transform: rotate(180deg);
	}

	.arrow.r {
		right: calc(var(--pad) - 22px);
	}

	${WIDE_ART} {
		.track > * {
			flex-basis: 270px;
		}
	}

	${PHONE} {
		.track {
			gap: 12px;
			padding-bottom: 24px;
		}

		.track > * {
			flex-basis: 250px;
		}

		.arrow {
			display: none;
		}
	}
`;

export const VsEndCard = styled.button`
	align-self: stretch;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 0;
	border: 0;
	border-radius: 18px;
	background: var(--panel-2);
	color: var(--teal-dark);
	cursor: pointer;

	i {
		font-family: var(--fd);
		font-style: normal;
		font-size: 16px;
		text-transform: uppercase;
		color: var(--ink);
	}

	${PHONE} {
		flex-basis: 160px !important;
	}
`;

/* kviz općeg znanja: the /premade »Glazba« band tint */
export const VsBand = styled.section`
	position: relative;
	overflow: hidden;
	margin-top: 84px;
	padding: 88px var(--pad) 92px;
	background: linear-gradient(
		120deg,
		${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(255, 126, 87, 0.06), rgba(255, 126, 87, 0.015) 55%'
				: 'rgba(241, 107, 70, 0.08), rgba(241, 107, 70, 0.02) 55%'},
		transparent
	);

	.lead {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 10px 24px;
	}

	h2 {
		margin: 0;
		font-size: clamp(39px, 4.4vw, 51px);
		line-height: 1;
	}

	h2 em {
		font-style: normal;
		color: var(--coral);
	}

	.hand {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(28px, 2.7vw, 31px);
		line-height: 1.1;
		color: var(--gold-ink);
		transform: rotate(-1.3deg);
		transform-origin: left;
	}

	.txt {
		max-width: 680px;
		margin: 17px 0 0;
		font-size: clamp(16.5px, 1.6vw, 18px);
		line-height: 1.5;
		color: var(--brown);
	}

	.picker {
		margin-top: 64px;
	}

	.row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 40px;
	}

	.nums {
		display: flex;
		align-items: flex-start;
		gap: clamp(0px, 5.3vw, 64px);
	}

	.nums button {
		padding: 0;
		border: 0;
		background: none;
		cursor: pointer;
		color: var(--mut);
	}

	.n {
		position: relative;
		isolation: isolate;
		display: block;
		font-family: var(--fd);
		font-size: clamp(52px, 7.1vw, 86px);
		line-height: 1;
		padding: 0 4px;
		transition: color 0.2s;
	}

	.n::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 6px;
		height: 16px;
		background: var(--teal);
		border-radius: 8px;
		z-index: -1;
		transform: scaleX(0);
		transform-origin: left;
		transition: transform 0.3s cubic-bezier(0.2, 0.65, 0.25, 1);
	}

	.nums button:hover .n,
	.nums .on .n {
		color: var(--ink);
	}

	.nums .on .n::after {
		transform: scaleX(1);
	}

	.add {
		flex-shrink: 0;
	}

	.add .cur {
		text-transform: uppercase;
	}

	.pool {
		margin-top: 64px;
	}

	${PHONE} {
		margin-top: 60px;
		padding-top: 64px;
		padding-bottom: 68px;

		h2 {
			font-size: 35px;
		}

		.hand {
			font-size: 26px;
		}

		.txt {
			margin-top: 14px;
		}

		.picker {
			margin-top: 44px;
			display: flex;
			flex-direction: column;
		}

		.row {
			display: contents;
		}

		.nums {
			order: 1;
			justify-content: space-between;
		}

		.n::after {
			height: 10px;
			bottom: 4px;
		}

		.pool {
			order: 2;
			margin-top: 44px;
		}

		.add {
			order: 3;
			width: 100%;
			margin-top: 36px;
		}
	}
`;

const dotRipple = keyframes`
	from {
		opacity: 0.1;
		transform: scale(0.35);
	}
	60% {
		opacity: 1;
		transform: scale(1.22);
	}
`;

const dotDraw = keyframes`
	from {
		background: rgba(var(--ink-rgb), 0.15);
		transform: scale(1);
	}
	55% {
		background: var(--teal);
		transform: scale(1.9);
	}
`;

export const VsPool = styled.div`
	.dots {
		display: grid;
		grid-template-columns: repeat(48, minmax(0, 1fr));
		gap: clamp(4px, 0.42vw, 6px);
	}

	.dots i {
		display: block;
		aspect-ratio: 1;
		border-radius: 50%;
		background: rgba(var(--ink-rgb), 0.15);
		/* waits for the Reveal around the pool, so it plays when it is seen */
		animation: ${dotRipple} 0.32s ease-out var(--ripple) both paused;
	}

	.rv.in & .dots i {
		animation-play-state: running;
	}

	.dots i.seen,
	.legend i.seen {
		background: transparent;
		box-shadow: inset 0 0 0 1px rgba(var(--ink-rgb), 0.28);
	}

	.dots i.on {
		background: var(--teal-dark);
		transform: scale(1.3);
		animation: ${dotDraw} 0.32s cubic-bezier(0.3, 1.4, 0.5, 1) var(--draw) both
			paused;
	}

	@media (prefers-reduced-motion: reduce) {
		.dots i,
		.rv.in & .dots i,
		.dots i.on {
			animation: none;
		}
	}

	.legend {
		display: flex;
		flex-wrap: wrap;
		gap: 8px 32px;
		margin-top: 24px;
		font-size: 16px;
		font-weight: 600;
		color: var(--mut);
	}

	.legend span {
		display: inline-flex;
		align-items: center;
		gap: 10px;
	}

	.legend i {
		display: block;
		width: 13px;
		height: 13px;
		border-radius: 50%;
		background: rgba(var(--ink-rgb), 0.2);
	}

	.legend i.on {
		background: var(--teal-dark);
	}

	.legend b {
		font-weight: 700;
		color: var(--ink);
	}

	${PHONE} {
		.dots {
			grid-template-columns: repeat(26, minmax(0, 1fr));
			gap: 3.5px;
		}

		.legend {
			flex-wrap: nowrap;
			justify-content: space-between;
			gap: 4px;
			margin-top: 16px;
			font-size: 10.25px;
			white-space: nowrap;
		}

		.legend span {
			gap: 4px;
		}

		.legend i {
			width: 7px;
			height: 7px;
			flex-shrink: 0;
		}
	}
`;

export const VsThemes = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 16px;

	button {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 14px;
		aspect-ratio: 4 / 3;
		padding: 12px;
		border: 0;
		border-radius: 14px;
		background: ${({ theme }) =>
			theme.mode === 'dark' ? '#232527' : 'var(--panel)'};
		color: ${({ theme }) => (theme.mode === 'dark' ? '#f7f7f7' : 'var(--ink)')};
		font-family: var(--fd);
		font-size: 17px;
		line-height: 1.12;
		text-transform: uppercase;
		text-align: center;
		cursor: pointer;
		transition:
			background 0.2s,
			transform 0.2s;
	}

	button:hover {
		background: ${({ theme }) =>
			theme.mode === 'dark' ? '#2d3033' : '#e3d9c9'};
		transform: translateY(-2px);
	}

	svg {
		font-size: 32px;
		color: ${({ theme }) =>
			theme.mode === 'dark' ? '#00ffc2' : 'var(--teal-dark)'};
	}

	${PHONE} {
		grid-template-columns: 1fr 1fr;
		gap: 12px;

		button {
			aspect-ratio: 1 / 0.82;
			font-size: 15.5px;
		}
	}
`;

/* the live /faq rows (FaqQRow) under a left-aligned heading */
export const VsFaq = styled.section`
	margin-top: 110px;

	.hd {
		padding: 0 var(--pad);
		margin-bottom: 44px;
	}

	.hand {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(25px, 2.5vw, 30px);
		line-height: 1.1;
		color: var(--gold-ink);
		transform: rotate(-1.4deg);
		margin-bottom: 16px;
	}

	h2 {
		margin: 0;
		font-size: clamp(39px, 4.4vw, 51px);
		line-height: 1;
	}

	h2 em {
		font-style: normal;
		color: var(--teal-dark);
	}

	.q h3 {
		font-size: clamp(19px, 2.2vw, 32px);
	}

	.a a {
		color: var(--teal-dark);
		font-weight: 600;
		text-decoration: underline;
		text-underline-offset: 3px;
	}

	${PHONE} {
		margin-top: 84px;

		.hd {
			margin-bottom: 28px;
		}

		h2 {
			font-size: 35px;
		}
	}
`;

export const VsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	align-items: start;
	gap: 28px 20px;
	margin-top: 22px;

	${WIDE_ART} {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	${PHONE} {
		grid-template-columns: minmax(0, 1fr);
		gap: 18px;
	}
`;

export const VsShelfHead = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 22px 0 10px;

	h1 {
		flex: 1;
		min-width: 0;
		margin: 0;
		font-size: clamp(24px, 3vw, 34px);
		line-height: 1;
	}

	.back {
		width: 38px;
		height: 38px;
		border-radius: 50%;
		border: 1.5px solid var(--hair);
		background: transparent;
		color: var(--ink);
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.back svg {
		transform: rotate(180deg);
	}
`;

export const VsFilterOpen = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 10px;
	flex-shrink: 0;
	padding: 0;
	border: 0;
	background: transparent;
	color: var(--ink);
	font-family: var(--fd);
	font-size: 15px;
	letter-spacing: 0.3px;
	text-transform: lowercase;
	cursor: pointer;

	.ic {
		position: relative;
		width: 38px;
		height: 38px;
		border-radius: 50%;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-size: 17px;
		transition: border-color 0.15s;
	}

	&:hover .ic {
		border-color: var(--ink);
	}
`;

export const VsFilterChips = styled.div`
	.lab {
		display: block;
		font-weight: 600;
		font-size: 12px;
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--mut);
		margin-bottom: 8px;
	}

	.chips {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 22px;
	}

	&.active {
		min-width: 0;
	}

	&.active .chips {
		margin: 0;
	}

	.removable {
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.author {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		min-height: 40px;
		padding: 4px 15px;
		border-radius: var(--r-pill);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		background: transparent;
		color: var(--ink);
		cursor: pointer;
		transition: 0.15s;
	}

	.author:hover {
		border-color: var(--ink);
	}

	/* no solid fill: the brand-colored logos would vanish on it */
	.author.on {
		border-color: var(--teal-dark);
		background: rgba(0, 200, 150, 0.14);
		box-shadow: inset 0 0 0 1px var(--teal-dark);
	}

	.author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 21px;
		line-height: 1;
		color: var(--teal-dark);
		white-space: nowrap;
	}

	.author-tint,
	.author-img {
		display: inline-flex;
		align-items: center;
	}

	.author-img img {
		display: block;
		height: 20px;
		width: auto;
	}

	.author-img.sq img {
		height: 26px;
	}

	.author-img .v-neon {
		display: none;
	}
`;

export const VsNone = styled.p`
	margin: 40px 0;
	text-align: center;
	color: var(--mut);
	font-size: 15px;
`;

/* details popup = the premade single page without tags and similar quizzes */
export const VsQuizPanel = styled.div`
	--pad: 48px;
	position: relative;
	display: flex;
	flex-direction: column;
	max-height: min(92vh, 900px);
	padding-top: 72px;
	overflow: hidden;
	background: var(--panel-2);
	border: 1px solid var(--hair);
	border-radius: 24px;
	box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
	text-align: left;

	.m-handle {
		display: none;
	}

	.body {
		overflow-y: auto;
		min-height: 0;
		color: var(--brown);
	}

	.qh {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 24px;
		padding: 0 48px 28px;
	}

	h1 {
		flex: 1 1 auto;
		min-width: 0;
		margin: 0;
		font-size: clamp(46px, 6vw, 76px);
		line-height: 0.94;
	}

	.hand {
		flex: 0 0 300px;
		display: block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 26px;
		line-height: 1.2;
		text-align: right;
		color: var(--gold-ink);
		transform: rotate(-1.6deg);
	}

	.qm {
		position: relative;
		height: 470px;
		margin: 0 16px;
		overflow: hidden;
		border-radius: 24px;
		background: #17130f;
	}

	.qm img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.shade {
		position: absolute;
		inset: 0 0 auto;
		height: 130px;
		pointer-events: none;
		background: linear-gradient(rgba(24, 14, 2, 0.62), transparent);
	}

	.spec {
		margin-top: 32px;
	}

	.spec .v.hand {
		flex: none;
		transform: none;
		text-align: left;
		font-size: 26px;
	}

	.qb {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 34px;
		padding: 34px 48px 32px;
	}

	.qb p {
		flex: 0 1 440px;
		margin: 0;
		font-size: 16.5px;
		line-height: 1.6;
		color: var(--brown);
	}

	.qb > button {
		flex-shrink: 0;
	}

	.tpl {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		padding: 0 48px 44px;
		border: 0;
		background: none;
		color: var(--mut);
		font: 500 15px/1.35 var(--fb);
		text-align: left;
		cursor: pointer;
	}

	.tpl svg {
		flex-shrink: 0;
		color: var(--teal-dark);
	}

	.tpl:hover {
		color: var(--ink);
	}

	/* the sheet the core Modal turns this into on touch layouts */
	${WIDE_ART} {
		--pad: 16px;

		.body {
			overscroll-behavior: contain;
		}

		max-height: 94dvh;
		padding-top: 14px;
		border-bottom: 0;
		border-radius: 24px 24px 0 0;

		.m-handle {
			display: block;
			flex-shrink: 0;
			width: 44px;
			height: 4px;
			border-radius: var(--r-pill);
			background: rgba(var(--ink-rgb), 0.18);
			margin: 0 auto 44px;
		}

		.qh {
			display: block;
			padding: 0 16px 18px;
		}

		.hand {
			margin-top: 20px;
			font-size: 24px;
			line-height: 1.25;
			text-align: left;
		}

		.qm {
			height: 240px;
			margin: 0 8px;
		}

		.spec {
			flex-wrap: wrap;
			margin-top: 28px;
		}

		.spec .cell {
			flex: 1 1 45%;
			padding: 18px 16px;
		}

		.spec .cell:nth-child(2) {
			border-right: 0;
		}

		.qb {
			display: block;
			padding: 28px 16px 24px;
		}

		.qb p {
			font-size: 16px;
			margin-bottom: 24px;
		}

		.qb > button {
			width: 100%;
		}

		.tpl {
			padding: 0 16px calc(32px + env(safe-area-inset-bottom));
		}
	}
`;
